// Angular
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
// Afidium
import { IIdentityInfo, ITraveller } from "libraries/service/resources/booking/interfaces/IGetBookings";
import { Yii } from "libraries/interfaces/Yii";
import { IDENTITY_DOCUMENT_EDITOR_PERMISSION } from "libraries/component/identity-info-editor/identity-info-editor.interface";
import { ILabels } from "libraries/interfaces/ILabels";
import { IdentityInfoEditorComponent } from "libraries/component/identity-info-editor/identity-info-editor.component";
import { PermissionResourceService } from "libraries/service/resources/permission/permissionResource.service";
import { FormControl, FormGroup } from "@angular/forms";
import { IconDefinition } from "@fortawesome/free-regular-svg-icons";
import { faPencilAlt, faTrash } from "@fortawesome/free-solid-svg-icons";

const IS_EDITING: string = 'isEditing';
const IS_DELETING: string = 'isDeleting';

@Component({
    selector: 'booking-traveller-detail',
    template: `
        <div class="details-container">
            <div class="col1">
                <!-- Identity Card / Passport / SFPD -->
                <div class="identity-card passport">
                    <!-- No edition, IdentityInfo exists in Traveller -->
                    <div class="noEdit"
                         *ngIf="!isEditing && !isDeleting && (traveller.passport || traveller.identityCard || traveller.secureFlightPassengerData)">
                        <identity-info [identities]="traveller|getIdentityInfo"
                                       [type]="traveller|getIdentityInfoDisplayMode"></identity-info>
                        <ng-container *permissionIf="identityDocumentEditorPermission">
                            <div class="iconDivDisplayed editButton" *ngIf="isEditable">
                                <btn class="button"
                                     type="actionInlineLink"
                                     [title]="wording.editTitle"
                                     (click)="onEditDocument()">
                                    <!--                                     icon="fa fa-pencil-alt"-->
                                    <fa-icon [icon]="pencil" class="icon"></fa-icon>
                                </btn>
                                <btn class="button"
                                     type="actionInlineLink"
                                     [title]="wording.deleteTitle"
                                     (click)="onDeleteDocument()">
                                    <fa-icon [icon]="trash" class="icon"></fa-icon>
<!--                                    icon="fa fa-trash"-->
                                </btn>
                            </div>
                        </ng-container>
                    </div>
                    <!-- Edition is ongoing -->
                    <div *ngIf="isEditing">
                        <div class="iconDiv editButton">
                            <btn class="button remove-button"
                                 type="actionInlineLink"
                                 icon="fa fa-times"
                                 [title]="wording.cancelTitle"
                                 (click)="cancelEditionAndDeletion()">
                            </btn>
                        </div>
                        <identity-info-editor #identityInfoEditorComponent
                                              [travellerFormGroup]="travellerFormGroup"
                                              [initialGender]="traveller.xTitle|titleToGender"
                                              [initialBirthDate]="traveller.birthDate|stringToDate"
                                              [initialCitizenship]="traveller.citizenShip"
                                              [initialIdentityInfoType]="traveller|getIdentityInfoDisplayMode"
                                              [initialPassport]="traveller.passport"
                                              [initialIdentityCard]="traveller.identityCard"
                                              [initialSfpd]="traveller.secureFlightPassengerData">
                        </identity-info-editor>
                    </div>
                    <!-- Missing IdentityInfo, it was missing in traveller or has just been deleted -->
                    <div class="noInformation"
                         *ngIf="isDeleting || (!isEditing && !traveller.passport && !traveller.identityCard && !traveller.secureFlightPassengerData)">
                        <ng-container *ngIf="isEditable && hasIdentityDocumentEditorPermission">
                            <btn type="actionInlineLink"
                                 icon="fa fa-plus"
                                 (click)="onEditDocument()"
                                 [label]="wording.addIdentity">
                            </btn>
                            <div class="iconDiv editButton" *ngIf="traveller.passport || traveller.identityCard || traveller.secureFlightPassengerData">
                                <btn class="remove-button"
                                     type="actionInlineLink"
                                     icon="fa fa-times"
                                     [title]="wording.cancelTitle"
                                     (click)="cancelEditionAndDeletion()">
                                </btn>
                            </div>
                        </ng-container>
                        <div class="noInformation" *ngIf="!isEditable || !hasIdentityDocumentEditorPermission">
                            {{wording.noIdentity}}
                        </div>
                    </div>
                </div>
            </div>
            <!--Visa-->
            <div class="col2">
                <div class="passport" *ngIf="traveller.visa">
                    <identity-info [identities]="traveller.visa" type="visa"></identity-info>
                </div>
                <div class="noInformation" *ngIf="!traveller.visa">
                    {{wording.noVisa}}
                </div>
            </div>
            <!--Frequent Traveller Card-->
            <div class="col3">
                <div class="passport" *ngIf="traveller.frequentTravellerCard">
                    <identity-info [identities]="traveller.frequentTravellerCard" type="frequentTravellerCard"></identity-info>
                </div>
                <div class="noInformation" *ngIf="!traveller.frequentTravellerCard">
                    {{wording.noFrequentTravellerCard}}
                </div>
            </div>
        </div>
    `,
    styles: [`
        .details-container {
            display: flex;
            padding: .75rem;
        }

        .col1, .col2, .col3 {
            flex: 1;
        }

        .col2 {
            margin-left: 0.75rem;
        }

        .col1 > div:not(:first-of-type), .col2 > div:not(:first-of-type), .col3 > div:not(:first-of-type) {
            margin-top: 10px;
        }

        .noInformation {
            color: #6E6E6E;
        }

        .iconDiv {
            display: flex;
            float: right;
            top: 0;
        }

        .iconDiv btn {
            margin-right: 0.75em;
        }

        .iconDivDisplayed {
            margin-left: auto;
            display: none;
            height: auto;
        }

        .noEdit:hover .iconDivDisplayed {
            display: flex;
        }

        .noEdit {
            display: flex;
            align-items: stretch;
            word-break: keep-all;
            width: 100%;
        }

        .editButton btn {
            margin-right: 0.75em;
        }
    `]
})
export class BookingTravellerDetailComponent implements OnChanges {

    @ViewChild("identityInfoEditorComponent")
    public passportFieldsetComponent: IdentityInfoEditorComponent;

    @Input()
    public traveller: ITraveller;
    /** A (possibly empty) FormGroup that will be used internally to handle form. */
    @Input()
    public travellerFormGroup: FormGroup;
    @Input()
    public isEditable: boolean;

    @Output()
    public readonly onFormPristineChange: EventEmitter<void> = new EventEmitter();

    get isEditing() : boolean {
        return this.travellerFormGroup.get(IS_EDITING).value;
    }
    set isEditing(value: boolean) {
        const control: FormControl = this.travellerFormGroup.get(IS_EDITING) as FormControl;
        control.setValue(value);
        if (value) {
            control.markAsDirty();
        } else {
            control.markAsPristine();
        }
    }

    get isDeleting(): boolean {
        return this.travellerFormGroup.get(IS_DELETING).value;
    }
    set isDeleting(value: boolean) {
        const control: FormControl = this.travellerFormGroup.get(IS_DELETING) as FormControl;
        control.setValue(value);
        if (value) {
            control.markAsDirty();
        } else {
            control.markAsPristine();
        }
    }

    public readonly identityDocumentEditorPermission: string = IDENTITY_DOCUMENT_EDITOR_PERMISSION;

    public readonly wording: ILabels = {
        noFrequentTravellerCard: Yii.t('wui-shared-js', "Aucune carte de fidelité"),
        noVisa: Yii.t('wui-shared-js', "Aucun Visa"),
        addIdentity: Yii.t('wui-shared-js', "Ajouter une pièce d'identité"),
        noIdentity: Yii.t('wui-shared-js', "Aucune pièce d'identité"),
        cancelTitle: Yii.t('wui-shared-js', "Annuler"),
        deleteTitle: Yii.t('wui-shared-js', "Supprimer"),
        editTitle: Yii.t('wui-shared-js', "Modifier"),
    };

    public hasIdentityDocumentEditorPermission: boolean;

    public travellerIdentityInfos: IIdentityInfo[];

    public pencil: IconDefinition = faPencilAlt;
    public trash: IconDefinition = faTrash;

    constructor(private readonly permissionService: PermissionResourceService) {
    }

    public ngOnChanges(changes: SimpleChanges): void {
        this.permissionService.hasPermission(IDENTITY_DOCUMENT_EDITOR_PERMISSION).subscribe(
            (value: boolean) => {
                this.hasIdentityDocumentEditorPermission = value;
            }
        );
        if (changes.traveller && changes.traveller.currentValue) {
            this.travellerIdentityInfos = this.getTravellerIdentityInfos(this.traveller);
        }

        if (!this.travellerFormGroup.contains(IS_EDITING)) {
            this.travellerFormGroup.addControl(IS_EDITING, new FormControl(false));
        }
        if (!this.travellerFormGroup.contains(IS_DELETING)) {
            this.travellerFormGroup.addControl(IS_DELETING, new FormControl(false));
        }
    }

    public getTravellerIdentityInfos(aTraveller: ITraveller): IIdentityInfo[] {
        if (aTraveller.passport && aTraveller.passport.length) {
            return aTraveller.passport;
        }
        if (aTraveller.identityCard && aTraveller.identityCard.length) {
            return aTraveller.identityCard;
        }
        if (aTraveller.secureFlightPassengerData) {
            return [{
                holder: aTraveller.secureFlightPassengerData.holder,
                number: null,
                placeOfIssue: null,
                validity: null
            }];
        }
        return [];
    }

    public onParentFormCheck(): void {
        if (this.passportFieldsetComponent) {
            this.passportFieldsetComponent.onParentFormCheck();
        }
    }

    public onParentCancel(): void {
        this.isEditing = false;
        this.isDeleting = false;
        this.travellerFormGroup.markAsPristine();
    }

    public getUpdatedTraveller(): ITraveller {
        if (this.isEditing) {
            return {
                ...this.traveller,
                xGender: this.passportFieldsetComponent.getXGender(),
                birthDate: this.passportFieldsetComponent.getBirthDateAsString(),
                citizenShip: this.passportFieldsetComponent.getCitizenShip(), // null for sfpd
                passport: this.passportFieldsetComponent.getPassportInArray(this.traveller.firstName, this.traveller.lastName),
                identityCard: this.passportFieldsetComponent.getIdentityCardInArray(this.traveller.firstName, this.traveller.lastName),
                secureFlightPassengerData: this.passportFieldsetComponent.getSecureFlightPassengerData(this.traveller.firstName, this.traveller.lastName)
            };
        }

        if (this.isDeleting) {
            return {
                ...this.traveller,
                passport: [],
                identityCard: [],
                secureFlightPassengerData: null
            };
        }

        return this.traveller;
    }

    public onEditDocument(): void {
        if (!this.isEditing) {
            this.isEditing = true;
            this.isDeleting = false;
            this.onFormPristineChange.emit();
        }
    }

    public onDeleteDocument(): void {
        if (!this.isDeleting) {
            this.isEditing = false;
            this.isDeleting = true;
            this.onFormPristineChange.emit();
        }
    }

    public cancelEditionAndDeletion(): void {
        if (this.isEditing || this.isDeleting) {
            this.isEditing = false;
            this.isDeleting = false;
            this.onFormPristineChange.emit();
        }
    }
}
